"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LANG, interpolate, translate, type LangCode } from "@/lib/i18n";
import { readGoogTrans } from "@/lib/googleTranslate";

type LanguageContextValue = {
  /** The active language, read from the same cookie the widget uses. */
  lang: LangCode;
  /**
   * Resolve a dot-path to a string.
   *
   * Deliberately NOT generic with a `string` default. A default type parameter
   * only applies when inference finds no candidate, and JSX child position
   * supplies one — inside a `motion.h2`, `t("cta.headingLine1")` would infer
   * `T` as framer-motion's `ReactNode | MotionValue` and then fail to assign.
   * Structured nodes go through `tv` instead, where the caller states the
   * shape explicitly and nothing can be inferred out from under it.
   */
  t: (path: string) => string;
  /** Resolve a dot-path to a list or object node — `tv<Step[]>("howItWorks.steps")`. */
  tv: <T>(path: string) => T;
  /** `t` with `{placeholder}` substitution. */
  tf: (path: string, values: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Serves the site's copy, in whichever language is active.
 *
 * HOW THE TWO HALVES DIVIDE UP
 *
 * The site translates two different kinds of text two different ways, and the
 * split is by who wrote it:
 *
 *   - Copy WE wrote lives in `lib/i18n/{en,hi}.ts` and is served from here, in
 *     hand-written Hindi. `.landing-root` carries `notranslate`, so the Google
 *     widget never touches any of it. This is what stops "Run your entire
 *     school" becoming "दौड़ना" (jogging) and the brand name being
 *     transliterated three different ways on three different pages.
 *
 *   - Copy we DIDN'T write — blog posts from the CMS, plan names and feature
 *     lists from the subscriptions API — has no dictionary entry and never
 *     will, because it changes without a deploy. Those regions opt back in
 *     with `translate="yes"` and the widget machine-translates them.
 *
 * WHY THE COOKIE
 *
 * Both halves read `googtrans`, the cookie the Google widget owns, so there is
 * exactly one answer to "what language is this page in". `LanguageToggle`
 * writes it and reloads; see `lib/googleTranslate.ts` for why the switch has
 * to be a reload rather than a live swap.
 *
 * First render is ALWAYS `DEFAULT_LANG`, matching what the server emitted, and
 * the cookie is applied in an effect straight after hydration. Reading it
 * during render instead would produce Hindi markup against English server HTML
 * and React would throw out the whole tree.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<LangCode>(DEFAULT_LANG);

  useEffect(() => {
    const active = readGoogTrans();
    if (active && active !== DEFAULT_LANG) setLang(active);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      t: (path) => translate<string>(lang, path),
      tv: <T,>(path: string) => translate<T>(lang, path),
      tf: (path, values) => interpolate(translate<string>(lang, path), values),
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Falls back to English rather than throwing when no provider is above it.
 *
 * Landing components get rendered outside `(landing)/layout` now and then —
 * error pages, a section lifted into the dashboard. A hard throw would turn
 * "this section is missing its provider" into "this page is blank", which is
 * the worse failure by a distance.
 */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;

  return {
    lang: DEFAULT_LANG,
    t: (path: string) => translate<string>(DEFAULT_LANG, path),
    tv: <T,>(path: string) => translate<T>(DEFAULT_LANG, path),
    tf: (path, values) => interpolate(translate<string>(DEFAULT_LANG, path), values),
  };
}

/** Shorthand for the common case — `const t = useT();`. */
export function useT() {
  return useLanguage().t;
}
